import React, { Suspense, lazy, useEffect, useState } from "react";
import { Routes, Route, useLocation } from "react-router-dom";

import useRouteTracking from "./components/useRouteTracking";
import Header from "./components/Header";
import Footer from "./components/Footer";
import Loader from "./components/Loader";

// Grouped HomePage components
const HeroSection = lazy(() => import("./components/HeroSection"));
const WhyChooseUs = lazy(() => import("./components/WhyChooseUs"));
const WellnessInspiration = lazy(() => import("./components/WellnessInspiration"));

// Core pages
const Packages = lazy(() => import("./components/Packages"));
const WelcomePage = lazy(() => import("./components/WelcomePage"));
const BookingBenefitsPage = lazy(() => import("./components/BookingBenefitsPage"));
const MeetingsPage = lazy(() => import("./components/MeetingsPage"));
const Accommodation = lazy(() => import("./components/Accommodation"));
const WellnessDayExperience = lazy(() => import("./components/WellnessDayExperience"));
const SaktiDiningRoom = lazy(() => import("./components/SaktiDiningRoom"));
const WellnessHealingCentre = lazy(() => import("./components/WellnessHealingCentre"));
const Contact = lazy(() => import("./components/Contact"));
const Gallery = lazy(() => import("./components/Gallery"));
const Blog = lazy(() => import("./components/Blog"));
const BlogDetail = lazy(() => import("./components/BlogDetail"));
const EnquiryForm = lazy(() => import("./components/EnquiryForm"));
const AboutUs = lazy(() => import("./components/AboutUs"));
const PrivacyPolicy = lazy(() => import("./components/PrivacyPolicy"));
const Glossary = lazy(() => import("./components/Glossary"));
const TermsConditions = lazy(() => import("./components/TermsConditions"));

// Country-specific resorts
const VedaFive = lazy(() => import("./components/VedaFive"));
const Atmaneem = lazy(() => import("./components/Atmaneem"));
const TheTravanCoreHeritage = lazy(() => import("./components/TheTravanCoreHeritage"));
const Kairali = lazy(() => import("./components/Kairali"));
const MekoshaAyurveda = lazy(() => import("./components/MekoshaAyurveda"));
const WellnessMahotsav = lazy(() => import("./components/WellnessMahotsav"));
const Deshadan = lazy(() => import("./components/Deshadan"));
const PemaWellness = lazy(() => import("./components/PemaWellness"));
const Aanandakosha = lazy(() => import("./components/Aanandakosha"));

// International retreats
const NamanRetreat = lazy(() => import("./components/NamanRetreat"));
const AvanaRetreat = lazy(() => import("./components/AvanaRetreat"));
const ThePlaceRetreat = lazy(() => import("./components/ThePlaceRetreat"));
const Blisshoian = lazy(() => import("./components/Blisshoian"));
const SoulsHineBali = lazy(() => import("./components/SoulsHineBali"));
const SumberKimaHill = lazy(() => import("./components/SumberKimaHill"));
const Desaseni = lazy(() => import("./components/Desaseni"));
const Samyama = lazy(() => import("./components/Samyama"));
const BambooVillage = lazy(() => import("./components/BambooVillage"));
const AleentaPhuket = lazy(() => import("./components/AleentaPhuket"));
const CasabayLuxuryPoolVillas = lazy(() => import("./components/CasabayLuxuryPoolVillas"));
const StayWellBeingAndLifestyleResort = lazy(() => import("./components/StayWellbeingAndLifestyleResort"));
const FiveElementsRetreatBali = lazy(() => import("./components/FiveElementsRetreatBali"));

const HomePage = () => (
  <>
    <HeroSection />
    <WhyChooseUs />
    <WellnessInspiration />
  </>
);

function AppContent() {
  const location = useLocation();
  const [routeLoading, setRouteLoading] = useState(false);

  useRouteTracking(); 

  useEffect(() => {
    setRouteLoading(true);
    const timer = setTimeout(() => setRouteLoading(false), 2000);
    return () => clearTimeout(timer);
  }, [location.pathname]);

  return (
    <>
      {routeLoading && <Loader />}
      <Header />
      <Suspense fallback={<Loader />}>
        <Routes>
          {/* Home & General */}
          <Route path="/" element={<HomePage />} />
          <Route path="/packages" element={<Packages />} />
          <Route path="/welcome" element={<WelcomePage />} />
          <Route path="/booking-benefits" element={<BookingBenefitsPage />} />
          <Route path="/meetings" element={<MeetingsPage />} />
          <Route path="/accommodations" element={<Accommodation />} />
          <Route path="/wellness-day-experience" element={<WellnessDayExperience />} />
          <Route path="/sakti-dining-room" element={<SaktiDiningRoom />} />
          <Route path="/wellness-healing-centre" element={<WellnessHealingCentre />} />
          <Route path="/contact" element={<Contact />} />
          <Route path="/gallery" element={<Gallery />} />
          <Route path="/about-us" element={<AboutUs />} />
          <Route path="/privacy-policy" element={<PrivacyPolicy />} />
          <Route path="/glossary" element={<Glossary />} />
          <Route path="/terms-conditions" element={<TermsConditions />} />
          <Route path="/enquiry" element={<EnquiryForm />} />

          {/* Blogs */}
          <Route path="/blog" element={<Blog />} />
          <Route path="/blogs/:id" element={<BlogDetail />} />

          {/* India Retreats */}
          <Route path="/india/veda-five" element={<VedaFive />} />
          <Route path="/india/atmaneem" element={<Atmaneem />} />
          <Route path="/india/the-travan-core-heritage" element={<TheTravanCoreHeritage />} />
          <Route path="/india/kairali" element={<Kairali />} />
          <Route path="/india/mekosha-ayurveda" element={<MekoshaAyurveda />} />
          <Route path="/india/wellness-mahotsav" element={<WellnessMahotsav />} />
          <Route path="/india/deshadan" element={<Deshadan />} />
          <Route path="/india/pema-wellness" element={<PemaWellness />} />
          <Route path="/india/aanandakosha" element={<Aanandakosha />} />

          {/* International Retreats */}
          <Route path="/vietnam/naman-retreat" element={<NamanRetreat />} />
          <Route path="/vietnam/avana-retreat" element={<AvanaRetreat />} />
          <Route path="/indonesia/the-place-retreat" element={<ThePlaceRetreat />} />
          <Route path="/vietnam/blisshoian" element={<Blisshoian />} />
          <Route path="/indonesia/souls-hine-bali" element={<SoulsHineBali />} />
          <Route path="/indonesia/sumber-kima-hill" element={<SumberKimaHill />} />
          <Route path="/indonesia/desaseni" element={<Desaseni />} />
          <Route path="/indonesia/samyama" element={<Samyama />} />
          <Route path="/vietnam/bamboo-village" element={<BambooVillage />} />
          <Route path="/thailand/aleenta-phuket" element={<AleentaPhuket />} />
          <Route path="/thailand/casabay-luxury-pool-villas" element={<CasabayLuxuryPoolVillas />} />
          <Route path="/thailand/stay-wellbeing-and-lifestyle-resort" element={<StayWellBeingAndLifestyleResort />} />
          <Route path="/indonesia/five-elements-retreat-bali" element={<FiveElementsRetreatBali />} />
        </Routes>
      </Suspense>
      <Footer />
    </>
  );
}

export default AppContent;
